import { useForm, useSelector } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  sidesQueryOptions,
  templateQueryOptions,
  updateTemplate,
} from '../../../../api/admin';

export const Route = createFileRoute('/_auth/admin/templates/$id')({
  component: TemplateEditPage,
});

function TemplateEditPage() {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const { data: svgData, isLoading } = useQuery(templateQueryOptions(id));
  useQuery(sidesQueryOptions());

  return (
    <section className="epi-card tone-blue">
      <div className="epi-card-head">
        <span className="num">04</span>
        <span className="title">
          {t('admin.templateEdit.title', 'Édition du template')}
        </span>
        <span className="meta">{id}</span>
      </div>

      <div className="mb-5">
        <Link to="/admin/templates" className="btn-link">
          ← {t('admin.templateEdit.backToList', 'Templates')}
        </Link>
      </div>

      {isLoading ? (
        <p className="label cursor py-6">{t('common.loading')}</p>
      ) : (
        // Keyed on the server's copy: the editor is the form, so a copy that
        // changed underneath us remounts it with the new text as its defaults.
        // Seeding it by `reset` instead would be undone on the next render —
        // `reset(values)` rewrites the form's defaultValues, and useForm
        // re-applies the ones passed in `useForm` after every render.
        <SvgEditor key={svgData?.svg ?? ''} id={id} svg={svgData?.svg ?? ''} />
      )}
    </section>
  );
}

/**
 * The SVG editor for one template. Mounted keyed on the server's copy, so its
 * `defaultValues` *are* the prefill — nothing seeds it after the fact, and
 * "cancel changes" is a plain argument-less `reset` back to those defaults.
 */
function SvgEditor({ id, svg }: { id: string; svg: string }) {
  const { t } = useTranslation();
  const captionId = useId();
  const queryClient = useQueryClient();
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const updateMutation = useMutation({
    mutationFn: (next: string) => updateTemplate(id, { svg: next }),
    onSuccess: () => {
      setSavedAt(Date.now());
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'templates', id],
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
    },
  });

  const form = useForm({
    defaultValues: { draft: svg },
    onSubmit: ({ value }) => {
      updateMutation.mutate(value.draft);
    },
  });
  const draft = useSelector(form.store, (state) => state.values.draft);
  const dirty = draft !== svg;

  const handleReset = () => {
    form.reset();
    setSavedAt(null);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="space-y-4"
    >
      <p id={captionId} className="label">
        {t('admin.templateEdit.svgEdit', 'SVG · édition')}
      </p>
      <form.Field name="draft">
        {(field) => (
          <textarea
            aria-labelledby={captionId}
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            spellCheck={false}
            className="mono min-h-[50vh] w-full resize-y border border-rule bg-bg-soft p-3 text-[11px]"
            // Inline on purpose: the unlayered `main textarea` rule in
            // index.css outranks a `text-*` colour class.
            // eslint-disable-next-line no-restricted-syntax -- see above
            style={{ color: 'var(--ink-soft)' }}
          />
        )}
      </form.Field>

      {updateMutation.error && (
        <p className="text-xs text-magenta">
          {updateMutation.error instanceof Error
            ? updateMutation.error.message
            : String(updateMutation.error)}
        </p>
      )}

      {savedAt !== null && !dirty && !updateMutation.isPending && (
        <p className="text-xs text-green-ink">
          {t('admin.templateEdit.saved', 'Modifications enregistrées')}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={!dirty || updateMutation.isPending}
          className="btn-primary disabled:opacity-50"
        >
          {updateMutation.isPending
            ? t('admin.templateEdit.saving', 'Enregistrement…')
            : t('admin.templateEdit.save', 'Enregistrer')}
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={!dirty || updateMutation.isPending}
          className="btn-ghost disabled:opacity-50"
        >
          {t('admin.templateEdit.reset', 'Annuler les modifications')}
        </button>
      </div>

      <div className="space-y-2">
        <p className="label">
          {t('admin.templateEdit.svgPreview', 'SVG · brut')}
        </p>
        <pre className="mono max-h-[40vh] overflow-auto border border-rule bg-bg-soft p-4 text-[11px] text-ink-soft">
          {draft}
        </pre>
      </div>
    </form>
  );
}
